import {
  BarChart3,
  Sparkles,
  ChevronDown,
  FileText,
  Globe,
  Import,
  Mail,
  Megaphone,
  Settings,
  User,
  Users,
} from "lucide-react";
import { CanAccess, useCanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import { GlobalSearchButton } from "../search/GlobalSearchButton";
import { useTeamUnread } from "../gula/team/useTeamFeed";
import { useMyPendingCalls } from "../gula/callPlan/useCallPlan";
import { useWaUnread } from "../gula/whatsapp/useWhatsapp";
import { activeSection } from "./headerSections";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();
  const teamUnread = useTeamUnread();
  const myPendingCalls = useMyPendingCalls();
  const waUnread = useWaUnread();

  const currentPath = activeSection(location.pathname);

  return (
    <>
      <nav className="grow">
        <header className="bg-secondary">
          <div className="px-4">
            <div className="flex justify-between items-center flex-1">
              <Link
                to="/"
                className="flex items-center gap-2 text-secondary-foreground no-underline"
              >
                <img
                  className="[.light_&]:hidden h-8"
                  src={darkModeLogo}
                  alt={title}
                />
                <img
                  className="[.dark_&]:hidden h-8"
                  src={lightModeLogo}
                  alt={title}
                />
                <h1 className="text-xl font-semibold">{title}</h1>
              </Link>
              <div>
                <nav className="flex">
                  <NavigationTab
                    label={translate("ra.page.dashboard")}
                    to="/"
                    isActive={currentPath === "/"}
                    badge={currentPath === "/" ? 0 : teamUnread}
                    badgeTitle="Mensajes nuevos del equipo"
                  />
                  {/* Setters already land on their call list in Inicio */}
                  <CanAccess resource="operations" action="list">
                    <NavigationTab
                      label="Mi jornada"
                      to="/mi-jornada"
                      isActive={currentPath === "/mi-jornada"}
                      badge={myPendingCalls}
                      badgeTitle="Llamadas pendientes en tu lista de hoy"
                      badgeClassName="bg-lime-300 text-neutral-900"
                    />
                  </CanAccess>
                  <NavigationTab
                    label="Clientes"
                    to="/clientes"
                    isActive={currentPath === "/clientes"}
                  />
                  <NavigationTab
                    label={translate("resources.contacts.name", {
                      smart_count: 2,
                    })}
                    to="/contacts"
                    isActive={currentPath === "/contacts"}
                  />
                  <NavigationTab
                    label="Mensajes"
                    to="/whatsapp"
                    isActive={currentPath === "/whatsapp"}
                    badge={waUnread}
                    badgeTitle="Conversaciones sin leer (WhatsApp, Instagram y Facebook)"
                    badgeClassName="bg-emerald-500 text-white"
                  />
                  <CanAccess resource="operations" action="list">
                    <NavigationTab
                      label="Operaciones"
                      to="/operaciones"
                      isActive={currentPath === "/operaciones"}
                    />
                  </CanAccess>
                  <CanAccess resource="money" action="list">
                    <NavigationTab
                      label="Dinero"
                      to="/dinero"
                      isActive={currentPath === "/dinero"}
                    />
                  </CanAccess>
                  <MarketingMenu isActive={currentPath === "marketing"} />
                </nav>
              </div>
              <div className="flex items-center">
                <GlobalSearchButton />
                <ThemeModeToggle />
                <RefreshButton />
                <UserMenu>
                  <ProfileMenu />
                  <CanAccess resource="sales" action="list">
                    <UsersMenu />
                  </CanAccess>
                  <CanAccess resource="configuration" action="edit">
                    <SettingsMenu />
                  </CanAccess>
                  <CanAccess resource="import" action="list">
                    <ImportFromJsonMenuItem />
                  </CanAccess>
                  <ChangelogMenuItem />
                </UserMenu>
              </div>
            </div>
          </div>
        </header>
      </nav>
    </>
  );
};

const tabClass = (isActive: boolean) =>
  `whitespace-nowrap px-5 py-3 text-sm font-medium transition-colors border-b-2 ${
    isActive
      ? "text-secondary-foreground border-secondary-foreground"
      : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
  }`;

/** Performance, email campaigns and web stats share one admin entry. */
const MarketingMenu = ({ isActive }: { isActive: boolean }) => {
  const { canAccess: canEmail } = useCanAccess({
    resource: "email",
    action: "list",
  });
  const { canAccess: canWeb } = useCanAccess({
    resource: "web",
    action: "list",
  });
  const { canAccess: canMoney } = useCanAccess({
    resource: "money",
    action: "list",
  });
  if (!canEmail && !canWeb && !canMoney) return null;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className={`${tabClass(isActive)} inline-flex items-center gap-1 outline-none`}
      >
        Marketing
        <ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-52">
        {canMoney ? (
          <DropdownMenuItem asChild>
            <Link to="/rendimiento" className="flex flex-col items-start gap-0">
              <span className="flex items-center gap-2 font-medium">
                <BarChart3 className="h-4 w-4" />
                Rendimiento
              </span>
              <span className="pl-6 text-xs text-muted-foreground">
                Tasa de cierre, anuncios, setters y analista IA
              </span>
            </Link>
          </DropdownMenuItem>
        ) : null}
        {canMoney ? (
          <DropdownMenuItem asChild>
            <Link
              to="/reactivacion"
              className="flex flex-col items-start gap-0"
            >
              <span className="flex items-center gap-2 font-medium">
                <Sparkles className="h-4 w-4" />
                Reactivación
              </span>
              <span className="pl-6 text-xs text-muted-foreground">
                Leads de Reactivar agrupados por dolor y objeción
              </span>
            </Link>
          </DropdownMenuItem>
        ) : null}
        {canEmail ? (
          <DropdownMenuItem asChild>
            <Link to="/parte" className="flex flex-col items-start gap-0">
              <span className="flex items-center gap-2 font-medium">
                <Megaphone className="h-4 w-4" />
                Parte del día
              </span>
              <span className="pl-6 text-xs text-muted-foreground">
                Imagen de las 9:00 para el grupo de setters
              </span>
            </Link>
          </DropdownMenuItem>
        ) : null}
        {canEmail ? (
          <DropdownMenuItem asChild>
            <Link to="/correo" className="flex flex-col items-start gap-0">
              <span className="flex items-center gap-2 font-medium">
                <Mail className="h-4 w-4" />
                Correo
              </span>
              <span className="pl-6 text-xs text-muted-foreground">
                Campañas y plantillas de email
              </span>
            </Link>
          </DropdownMenuItem>
        ) : null}
        {canWeb ? (
          <DropdownMenuItem asChild>
            <Link to="/web" className="flex flex-col items-start gap-0">
              <span className="flex items-center gap-2 font-medium">
                <Globe className="h-4 w-4" />
                Web
              </span>
              <span className="pl-6 text-xs text-muted-foreground">
                Visitas, mapas de calor y chatbot
              </span>
            </Link>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NavigationTab = ({
  label,
  to,
  isActive,
  badge = 0,
  badgeTitle,
  badgeClassName = "bg-red-600 text-white",
}: {
  label: string;
  to: string;
  isActive: boolean;
  badge?: number;
  badgeTitle?: string;
  badgeClassName?: string;
}) => (
  <Link to={to} className={tabClass(isActive)}>
    {label}
    {badge > 0 ? (
      <span
        title={badgeTitle}
        className={`ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold ${badgeClassName}`}
      >
        {badge > 99 ? "99+" : badge}
      </span>
    ) : null}
  </Link>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};
export default Header;
